import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, Clock, ArrowLeft, ChevronRight, AlertTriangle, Shield } from 'lucide-react';
import { format, addDays, isSameDay, getDay } from 'date-fns';
import { cn, formatTime12Hour } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  TIMEZONES,
  getTimezoneAbbr,
  convertTimeToTimezone,
  getStoredTimezone,
  saveTimezonePreference,
} from "@/lib/timezone-utils";
import { fromZonedTime } from 'date-fns-tz';

interface TimeSlot {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface AvailableSlot {
  date: Date;
  start_time: string;
  end_time: string;
  timeSlotId: string;
}

interface StreamlinedBookingFlowProps {
  mentorId: string;
  mentorName: string;
  onTimeSlotSelect: (slot: AvailableSlot) => void;
  selectedSlot?: AvailableSlot | null;
  mentorPlatform?: string;
  backgroundCheckStatus?: string;
}

type BookingStep = 'date' | 'time';

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const StreamlinedBookingFlow: React.FC<StreamlinedBookingFlowProps> = ({
  mentorId,
  mentorName,
  onTimeSlotSelect,
  selectedSlot,
  mentorPlatform,
  backgroundCheckStatus
}) => {
  const [currentStep, setCurrentStep] = useState<BookingStep>('date');
  const [availability, setAvailability] = useState<TimeSlot[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [loading, setLoading] = useState(true);
  const [datesWithAvailability, setDatesWithAvailability] = useState<Date[]>([]);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(getStoredTimezone());
  const [mentorTimezone, setMentorTimezone] = useState<string | null>(null);
  const { toast } = useToast();
  
  const timezoneAbbr = getTimezoneAbbr(selectedTimezone);

  useEffect(() => {
    fetchMentorTimezone();
    fetchAvailability();
    fetchBlackoutDates();
  }, [mentorId]);

  useEffect(() => {
    // Only generate slots when we have mentor timezone AND availability
    if (availability.length > 0 && mentorTimezone) {
      generateDatesWithAvailability();
      if (selectedDate) {
        generateAvailableSlots();
      }
    }
  }, [availability, selectedDate, mentorTimezone]);

  const fetchMentorTimezone = async () => {
    try {
      const { data, error } = await supabase
        .from('mentors')
        .select('timezone')
        .eq('id', mentorId)
        .single();

      if (error) throw error;
      if (data?.timezone) {
        setMentorTimezone(data.timezone);
      } else {
        toast({
          title: 'Timezone Not Set',
          description: 'This mentor has not configured their timezone. Please contact support.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error fetching mentor timezone:', error);
      toast({
        title: 'Error',
        description: 'Failed to load mentor timezone information',
        variant: 'destructive',
      });
    }
  };

  const fetchAvailability = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_availability')
        .select('*')
        .eq('mentor_id', mentorId)
        .eq('is_active', true)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) throw error;
      setAvailability(data || []);
    } catch (error: any) {
      console.error('Error fetching availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to load mentor availability',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBlackoutDates = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_blackout_dates')
        .select('blackout_date')
        .eq('mentor_id', mentorId);

      if (error) throw error;
      setBlackoutDates((data || []).map(d => d.blackout_date));
    } catch (error: any) {
      console.error('Error fetching blackout dates:', error);
    }
  };

  const generateDatesWithAvailability = () => {
    const dates: Date[] = [];
    const today = new Date();
    
    // Generate next 30 days
    for (let i = 0; i < 30; i++) {
      const date = addDays(today, i);
      const dayOfWeek = getDay(date);
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Check if mentor has availability on this day and it's not a blackout date
      const hasAvailability = availability.some(slot => slot.day_of_week === dayOfWeek);
      const isBlackedOut = blackoutDates.includes(dateStr);
      
      if (hasAvailability && !isBlackedOut) {
        dates.push(date);
      }
    }
    
    setDatesWithAvailability(dates);
  };

  const generateAvailableSlots = async () => {
    if (!selectedDate || !mentorTimezone) return;

    try {
      // Get existing sessions for the selected date to exclude booked times
      const { data: sessions, error } = await supabase
        .from('sessions')
        .select('scheduled_date, scheduled_start_time, scheduled_end_time')
        .eq('mentor_id', mentorId)
        .eq('scheduled_date', format(selectedDate, 'yyyy-MM-dd'))
        .in('status', ['scheduled', 'in_progress']);

      if (error) throw error;

      const dayOfWeek = getDay(selectedDate);
      const dayAvailability = availability.filter(slot => slot.day_of_week === dayOfWeek);
      
      const slots: AvailableSlot[] = [];

      dayAvailability.forEach(timeSlot => {
        // Respect exact minutes from availability and generate 60-min slots aligned to start
        const parseToMinutes = (t: string) => {
          const parts = t.split(":");
          const h = parseInt(parts[0], 10) || 0;
          const m = parseInt(parts[1], 10) || 0;
          return h * 60 + m;
        };
        const toHHMM = (mins: number) => {
          const h = Math.floor(mins / 60) % 24;
          const m = mins % 60;
          return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        };

        const SLOT_MINUTES = 60; // keep existing 1h UI behavior
        const startMins = parseToMinutes(timeSlot.start_time);
        const endMins = parseToMinutes(timeSlot.end_time);

        for (let t = startMins; t + SLOT_MINUTES <= endMins; t += SLOT_MINUTES) {
          const startTime = toHHMM(t);
          const endTime = toHHMM(t + SLOT_MINUTES);

          // Check if this time slot is not already booked
          const isBooked = sessions?.some(session =>
            session.scheduled_start_time === startTime || (typeof session.scheduled_start_time === 'string' && session.scheduled_start_time.startsWith(startTime))
          );

          // Skip past times - interpret the time in MENTOR's timezone, then check if past
          const slotDateTimeUtc = fromZonedTime(`${format(selectedDate, 'yyyy-MM-dd')}T${startTime}:00`, mentorTimezone);
          const isPast = slotDateTimeUtc.getTime() <= Date.now();

          if (!isBooked && !isPast) {
            slots.push({
              date: selectedDate,
              start_time: startTime,
              end_time: endTime,
              timeSlotId: timeSlot.id
            });
          }
        }
      });

      setAvailableSlots(slots);
    } catch (error: any) {
      console.error('Error generating available slots:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available time slots',
        variant: 'destructive',
      });
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setCurrentStep('time');
    }
  };

  const handleTimeSlotSelect = (slot: AvailableSlot) => {
    if (!mentorTimezone) {
      toast({
        title: 'Timezone Not Available',
        description: 'Cannot book session - mentor timezone is not set. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }
    const dateStr = format(slot.date, 'yyyy-MM-dd');
    // Convert mentor's time from MENTOR's timezone to UTC (slot times are in mentor TZ)
    const utcDateTime = fromZonedTime(`${dateStr}T${slot.start_time}:00`, mentorTimezone);
    onTimeSlotSelect({ ...slot, utcDateTime, mentorTimezone, selectedTimezone } as any);
  };

  const handleTimezoneChange = (timezone: string) => {
    setSelectedTimezone(timezone);
    saveTimezonePreference(timezone);
  };

  const goBackToDateSelection = () => {
    setCurrentStep('date');
    setSelectedDate(undefined);
    onTimeSlotSelect(null as any); // Clear selection
  };

  const isInPersonBlocked = mentorPlatform === 'in-person' && backgroundCheckStatus !== 'approved';

  if (loading || !mentorTimezone) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            {loading ? 'Loading mentor\'s schedule...' : 'Loading timezone information...'}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show background check warning if mentor prefers in-person but isn't approved
  if (isInPersonBlocked) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Background Check Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto" />
            <div>
              <h3 className="font-medium text-lg mb-2">In-Person Sessions Unavailable</h3>
              <p className="text-muted-foreground">
                This mentor prefers in-person sessions but hasn't completed the required background check process yet.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Please check back later or contact the mentor about alternative session formats.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <div className="space-y-4">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2" />
              {currentStep === 'date' ? 'Select a Date' : 'Select a Time'}
            </div>
            {currentStep === 'time' && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={goBackToDateSelection}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Times shown in</span>
            <Select value={selectedTimezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger className="w-[200px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-hidden">
        {currentStep === 'date' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose a date when {mentorName} is available
            </p>
            <div className="flex justify-center overflow-x-auto pb-2">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date) => {
                  // Disable past dates and dates without availability
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isInPast = date < today;
                  const hasAvailability = datesWithAvailability.some(availableDate => 
                    isSameDay(date, availableDate)
                  );
                  return isInPast || !hasAvailability;
                }}
                initialFocus
                className="pointer-events-auto border rounded-lg"
                modifiers={{
                  available: datesWithAvailability
                }}
                modifiersStyles={{
                  available: {
                    backgroundColor: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                    fontWeight: 'bold'
                  }
                }}
              />
            </div>
            <div className="text-center">
              <Badge variant="outline" className="text-xs">
                Highlighted dates show availability
              </Badge>
            </div>
          </div>
        )}

        {currentStep === 'time' && selectedDate && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="font-medium text-lg mb-2">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </h3>
              <p className="text-sm text-muted-foreground">
                Select your preferred time slot
              </p>
            </div>

            {availableSlots.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No available time slots for this date
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goBackToDateSelection}
                  className="mt-4"
                >
                  Choose a Different Date
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableSlots.map((slot, index) => {
                    const isSelected = selectedSlot && 
                      isSameDay(slot.date, selectedSlot.date) &&
                      slot.start_time === selectedSlot.start_time;

                    return (
                      <Button
                        key={index}
                        variant={isSelected ? "default" : "outline"}
                        size="lg"
                        onClick={() => handleTimeSlotSelect(slot)}
                        className="h-auto min-h-[3rem] py-3 px-4 text-sm sm:text-base flex-wrap sm:flex-nowrap justify-center"
                      >
                        <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="whitespace-nowrap">
                          {formatTime12Hour(
                            convertTimeToTimezone(
                              slot.date,
                              slot.start_time,
                              mentorTimezone,
                              selectedTimezone
                            )
                          )}
                        </span>
                        <ChevronRight className="w-3 h-3 mx-1 flex-shrink-0" />
                        <span className="whitespace-nowrap">
                          {formatTime12Hour(
                            convertTimeToTimezone(
                              slot.date,
                              slot.end_time,
                              mentorTimezone,
                              selectedTimezone
                            )
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          {timezoneAbbr}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                
                {selectedSlot && (
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <h4 className="font-medium mb-1">Selected Time Slot</h4>
                    <p className="text-sm text-muted-foreground">
                      {format(selectedSlot.date, 'EEEE, MMMM d')} at {formatTime12Hour(
                        convertTimeToTimezone(
                          selectedSlot.date,
                          selectedSlot.start_time,
                          mentorTimezone,
                          selectedTimezone
                        )
                      )} - {formatTime12Hour(
                        convertTimeToTimezone(
                          selectedSlot.date,
                          selectedSlot.end_time,
                          mentorTimezone,
                          selectedTimezone
                        )
                      )} {timezoneAbbr}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StreamlinedBookingFlow;